import { useState, useMemo, useEffect } from "react"
import { useRouter } from "next/router"
import { Box, Heading, Accordion, Button } from "@chakra-ui/react"
import { FaChevronDown, FaChevronUp } from "react-icons/fa"

import Wrapper from "components/layout/wrapper"
import { scrollTo } from "src/util/helpers"

const buttonStyles = {
    default: {
        color: "#fff",
        fontWeight: 600,
    },
    largeHead: {
        color: "color4",
        fontWeight: 600,
        fontSize: "20px",
    },
}

const caretStyles = {
    marginLeft: "10px",
    marginTop: "3px",
    float: "right",
}

export default function AccordionComponent({ block, colorMode }) {
    const router = useRouter()
    const [allExpanded, setAllExpanded] = useState(false)
    const [expandedItems, setExpandedItems] = useState([])
    const allIndices = useMemo(() => Array.from(Array(block.accordion.length).keys()), [block.accordion])

    // open specific accordion item if query param is present, 0-based
    useEffect(() => {
        if (!router.query.show || !block.identifier) return

        const lastDashIndex = router.query.show.lastIndexOf("-")

        const accordionId = router.query.show.slice(0, lastDashIndex)
        const itemId = router.query.show.slice(lastDashIndex + 1)

        if (accordionId !== block.identifier) return

        toggleOne(parseInt(itemId))

        // if we have a hash in the URL, scroll to it
        if (window.location.hash) {
            setTimeout(() => {
                const element = document.getElementById(window.location.hash.slice(1))
                if (element) {
                    scrollTo(element)
                }
            }, 200)
        }
        // otherwise, scroll to the accordion item
        else {
            setTimeout(() => {
                const element = document.getElementById(`accordion-button-${block.identifier}-${itemId}`)
                if (element) {
                    scrollTo(element)
                }
            }, 200)
        }
    }, [router.query])

    const toggleAll = () => {
        setExpandedItems(allExpanded ? [] : allIndices)
        setAllExpanded(current => !current)
    }

    // On mobile, when an item is being opened, keep its header in view below the
    // sticky header instead of letting the browser scroll the focused trigger up.
    const keepHeaderInView = (j, event) => {
        const willExpand = !expandedItems.includes(j)
        if (!willExpand) return
        if (typeof window !== "undefined" && window.innerWidth > 831) return

        const trigger = event.currentTarget
        // let the content expand first so the scroll target position is stable
        setTimeout(() => scrollTo(trigger), 60)
    }

    const toggleOne = tabIndex => {
        setExpandedItems(current => {
            const position = expandedItems.indexOf(tabIndex)
            // remove index from array if exists, add index to array if not exists
            if (position === -1) {
                current.push(tabIndex)
            } else {
                current.splice(position, 1)
            }
            return [...current]
        })
    }

    return (
        <Wrapper className={"body colorMode--" + colorMode + " " + block.cssClasses} id={block.identifier}>
            {block.showExpandAllButton && (
                <Button
                    as={"a"}
                    onClick={toggleAll}
                    className={"btn btn-small btn-expand"}
                    borderRadius={0}
                    fontWeight={500}
                    bgColor={"var(--button-bgcolor)"}
                    color={"var(--button-color)"}
                    _hover={{ bgColor: "var(--button-bgcolor-hover)" }}
                    height={"auto"}
                    fontSize={"18px"}
                    mb={"0px"}
                >
                    {allExpanded ? "Collapse All " : "Expand All"}
                    {allExpanded && block.cssClasses?.includes("show-caret") ? (
                        <FaChevronUp size={20} style={caretStyles} />
                    ) : !allExpanded && block.cssClasses?.includes("show-caret") ? (
                        <FaChevronDown size={20} style={caretStyles} />
                    ) : (
                        ""
                    )}
                </Button>
            )}
            <Accordion.Root
                multiple
                value={expandedItems.map(i => i.toString())}
                onValueChange={details => setExpandedItems(details.value.map(v => parseInt(v)))}
            >
                {block.accordion.map((section, j) => {
                    return (
                        <Accordion.Item key={j} borderColor="#666" id={`${block.identifier}-${j}`} value={j.toString()}>
                            <Heading as="h4" mt={"0px !important"} color="white" fontFamily="heading">
                                <Accordion.ItemTrigger
                                    p="15px 0"
                                    cursor="pointer"
                                    gap={1}
                                    _focus={{ outline: 0 }}
                                    _hover={{ bgColor: "blackAlpha.50" }}
                                    onClick={event => keepHeaderInView(j, event)}
                                >
                                    <Accordion.ItemIndicator color="color1" />
                                    <Box
                                        className={
                                            block.style === "largeHead" ? "accordion-label-large" : "accordion-label"
                                        }
                                        flex="1"
                                        textAlign="left"
                                        pl={1}
                                        {...buttonStyles[block.style]}
                                        dangerouslySetInnerHTML={{ __html: section.label }}
                                    ></Box>
                                </Accordion.ItemTrigger>
                            </Heading>
                            <Accordion.ItemContent pb={4} pl={0}>
                                <Box
                                    p="5px 15px 20px 24px"
                                    className="body"
                                    dangerouslySetInnerHTML={{ __html: section.body }}
                                />
                            </Accordion.ItemContent>
                        </Accordion.Item>
                    )
                })}
            </Accordion.Root>
        </Wrapper>
    )
}
